import {
  SizeBreakpointLg,
  SizeBreakpointMd,
  SizeBreakpointSm,
  SizeBreakpointXl,
  SizeBreakpointXs,
  SizeBreakpointXxl,
  SizeBreakpointXxs,
  SizeSpacing0,
  SizeSpacing1,
  SizeSpacing2,
  SizeSpacing3,
  SizeSpacing4,
  SizeSpacing5,
  SizeSpacing6,
  SizeSpacing7,
  SizeSpacing8,
  SizeSpacing9,
  SizeSpacing10,
  SizeSpacing11,
  SizeSpacing12,
  SizeSpacing13,
  SizeSpacing14,
  SizeSpacing15,
  SizeSpacing16,
  SizeSpacing17,
  SizeSpacing18,
  SizeSpacing19,
  SizeSpacing20,
  SizeSpacingHalf,
  SizeSpacingPx,
  SizeSpacingQuarter,
} from '@snowball-tech/design-tokens/dist/web/typescript/design-tokens'

import type { CSSProperties } from 'react'

import isEmpty from 'lodash/fp/isEmpty'
import isNumber from 'lodash/fp/isNumber'
import isString from 'lodash/fp/isString'

import { Logger } from '../helpers/log'

/** @public */
export const BASE_SPACING_VALUE = 8
/** @public */
export const REM_BASE = BASE_SPACING_VALUE * 2

/** @public */
export const unstyledButtonClassNames =
  'bg-[unset] border-0 text-left px-0 py-0 padding cursor-pointer'

/** @public */
export function asPx(
  value?: number | string,
  { base = REM_BASE, withUnit = false } = {},
) {
  if (
    isString(value) &&
    `${Number.parseInt(value, 10)}` !== value &&
    !value.includes('px') &&
    !value.includes('rem')
  ) {
    console.warn(`[asPx] Value "${value}" is neither rem nor px!`)

    return value
  }

  if (isNumber(value)) {
    return withUnit ? `${value}px` : value
  }

  if (isEmpty(value)) {
    return 0
  }

  try {
    const intValue = Number.parseFloat(value)

    if (value.includes('rem')) {
      const computedValue = intValue * base

      return withUnit ? `${computedValue}px` : computedValue
    }

    return withUnit ? `${intValue}px` : intValue
  } catch {
    return 0
  }
}

/** @public */
export const spacings = {
  0: asPx(SizeSpacing0) as number,

  px: asPx(SizeSpacingPx) as number,

  quarter: asPx(SizeSpacingQuarter) as number,

  half: asPx(SizeSpacingHalf) as number,

  1: asPx(SizeSpacing1) as number,
  2: asPx(SizeSpacing2) as number,
  3: asPx(SizeSpacing3) as number,
  4: asPx(SizeSpacing4) as number,
  5: asPx(SizeSpacing5) as number,
  6: asPx(SizeSpacing6) as number,
  7: asPx(SizeSpacing7) as number,
  8: asPx(SizeSpacing8) as number,
  9: asPx(SizeSpacing9) as number,
  10: asPx(SizeSpacing10) as number,
  11: asPx(SizeSpacing11) as number,
  12: asPx(SizeSpacing12) as number,
  13: asPx(SizeSpacing13) as number,
  14: asPx(SizeSpacing14) as number,
  15: asPx(SizeSpacing15) as number,
  16: asPx(SizeSpacing16) as number,
  17: asPx(SizeSpacing17) as number,
  18: asPx(SizeSpacing18) as number,
  19: asPx(SizeSpacing19) as number,
  20: asPx(SizeSpacing20) as number,
}

/** @public */
export const breakpoints = {
  xxs: {
    px: asPx(SizeBreakpointXxs) as number,
    value: Number.parseInt(SizeBreakpointXxs, 10),
  },

  xs: {
    px: asPx(SizeBreakpointXs) as number,
    value: Number.parseInt(SizeBreakpointXs, 10),
  },

  sm: {
    px: asPx(SizeBreakpointSm) as number,
    value: Number.parseInt(SizeBreakpointSm, 10),
  },

  md: {
    px: asPx(SizeBreakpointMd) as number,
    value: Number.parseInt(SizeBreakpointMd, 10),
  },

  lg: {
    px: asPx(SizeBreakpointLg) as number,
    value: Number.parseInt(SizeBreakpointLg, 10),
  },

  xl: {
    px: asPx(SizeBreakpointXl) as number,
    value: Number.parseInt(SizeBreakpointXl, 10),
  },

  xxl: {
    px: asPx(SizeBreakpointXxl) as number,
    value: Number.parseInt(SizeBreakpointXxl, 10),
  },
}

/** @public */
export function classNameToLegacyStyle(
  className?: string | null,
  extendedStyles?: CSSProperties | null,
  logger?: Logger,
): CSSProperties {
  if (isEmpty(className)) {
    return extendedStyles || {}
  }

  const FORBIDDEN_CLASS_NAMES: Set<string> = new Set([
    'absolute',
    'collapse',
    'decoration-0',
    'decoration-1',
    'decoration-2',
    'decoration-4',
    'decoration-8',
    'decoration-auto',
    'decoration-from-font',
    'fixed',
    'flex-col',
    'flex-nowrap',
    'grid',
    'inline-grid',
    'invisible',
    'relative',
    'rounded-s',
    'static',
    'sticky',
    'text-balance',
    'text-nowrap',
    'text-pretty',
    'text-wrap',
    'visible',
  ])
  const FORBIDDEN_CLASS_NAMES_PREFIX: Array<string> = [
    '-bottom-',
    '-indent-',
    '-left-',
    '-m-',
    '-mb-',
    '-me-',
    '-ml-',
    '-mr-',
    '-ms-',
    '-mt-',
    '-mx-',
    '-my-',
    '-pe-',
    '-ps-',
    '-right-',
    '-top-',
    'accent-',
    'active:',
    'after:',
    'animate-',
    'aspect-',
    'auto-',
    'backdrop-',
    'before:',
    'bottom-',
    'checked:',
    'col-',
    'cursor-',
    'delay-',
    'duration-',
    'ease-',
    'even:',
    'filter',
    'first-letter:',
    'first-line:',
    'first-of-type:',
    'first:',
    'flex-',
    'focus:',
    'grid-',
    'hover:',
    'hyphens-',
    'items-',
    'justify-',
    'last-of-type:',
    'last:',
    'left-',
    'list-image-',
    'marker:',
    'me-',
    'ms-',
    'odd:',
    'only-of-type:',
    'only:',
    'origin-',
    'outline-offset-',
    'pe-',
    'placeholder:',
    'ps-',
    'resize',
    'right-',
    'rounded-b',
    'rounded-bl',
    'rounded-br',
    'rounded-e',
    'rounded-ee',
    'rounded-l',
    'rounded-r',
    'rounded-s-',
    'rounded-se',
    'rounded-ss',
    'rounded-t',
    'rounded-tl',
    'rounded-tr',
    'row-',
    'select-',
    'shadow',
    'snap-',
    'target:',
    'top-',
    'transform',
    'transition',
    'underline-offset-',
    'visited:',
    'z-',
  ]
  const FORBIDDEN_CLASS_NAMES_CONTENT: Array<string> = [
    '::after',
    '::before',
    '::first-letter',
    '::first-line',
    '::marker',
    '::placeholder',
    ':active',
    ':checked',
    ':first-child',
    ':first-of-type',
    ':focus',
    ':has(',
    ':hover',
    ':last-child',
    ':last-of-type',
    ':link',
    ':not',
    ':nth-child',
    ':nth-last-child',
    ':nth-last-of-type',
    ':nth-of-type',
    ':only-child',
    ':only-of-type',
    ':target',
    ':visited',
    'block-size',
    'border-block',
    'border-image',
    'border-inline',
    'clip-path',
    'color-scheme',
    'conic-gradient(',
    'font-face',
    'gap:',
    'hsla(',
    'import(',
    'inline-size',
    'keyframes',
    'lab(',
    'lang(',
    'lch(',
    'list-style-image',
    'max-block-size',
    'min-inline-size',
    'oklabl(',
    'oklch(',
    'overflow-wrap',
    'row-gap',
    'shape-margin',
    'shape-outside',
    'tab-size',
    'text-shadow',
    'word-wrap',
  ]

  const WARNING_CLASS_NAMES: Set<string> = new Set([
    'bg-auto',
    'bg-contain',
    'bg-cover',
    'flex',
    'flow-root',
    'h-fit',
    'h-max',
    'h-min',
    'h-screen',
    'inline-flex',
    'max-h-fit',
    'max-h-max',
    'max-h-min',
    'max-h-screen',
    'max-w-fit',
    'max-w-max',
    'max-w-min',
    'min-h-fit',
    'min-h-max',
    'min-h-min',
    'min-h-screen',
    'min-w-fit',
    'min-w-max',
    'min-w-min',
    'overflow-ellipsis',
    'text-clip',
    'text-ellipsis',
    'text-end',
    'text-start',
    'truncate',
    'w-fit',
    'w-max',
    'w-min',
  ])
  const WARNING_CLASS_NAMES_PREFIX: Array<string> = [
    'bg-blend-',
    'bg-clip-',
    'bg-origin-',
    'break-',
    'decoration-',
    'mix-blend-',
    'object-',
    'whitespace-',
  ]
  const WARNING_CLASS_NAMES_CONTENT: Array<string> = [
    'calc(',
    'clamp(',
    'display:',
    'font-stretch',
    'initial',
    'max(',
    'min(',
    'text-emphasis',
    'text-emphasis-position',
    'text-underline-position',
    'writing-mode',
  ]

  const IGNORED_CLASS_NAMES: Set<string> = new Set([])
  const IGNORED_CLASS_NAMES_PREFIX: Array<string> = []
  const IGNORED_CLASS_NAMES_CONTENT: Array<string> = []

  const classNames = className.split(' ')

  const styles: CSSProperties = {}
  classNames.forEach((classNameBit) => {
    if (
      FORBIDDEN_CLASS_NAMES.has(classNameBit) ||
      FORBIDDEN_CLASS_NAMES_PREFIX.some((prefix) =>
        classNameBit.startsWith(prefix),
      ) ||
      FORBIDDEN_CLASS_NAMES_CONTENT.some((content) =>
        classNameBit.includes(content),
      )
    ) {
      ;(logger ?? console).error(
        `ClassName "${classNameBit}" isn't supported in legacy mode!`,
      )

      return
    }

    if (
      WARNING_CLASS_NAMES.has(classNameBit) ||
      WARNING_CLASS_NAMES_PREFIX.some((prefix) =>
        classNameBit.startsWith(prefix),
      ) ||
      WARNING_CLASS_NAMES_CONTENT.some((content) =>
        classNameBit.includes(content),
      )
    ) {
      ;(logger ?? console).warn(
        `ClassName "${classNameBit}" isn't fully supported in all legacy (mail) client! You should consider using another option.`,
      )

      return
    }

    if (
      IGNORED_CLASS_NAMES.has(classNameBit) ||
      IGNORED_CLASS_NAMES_PREFIX.some((prefix) =>
        classNameBit.startsWith(prefix),
      ) ||
      IGNORED_CLASS_NAMES_CONTENT.some((content) =>
        classNameBit.includes(content),
      )
    ) {
      ;(logger ?? console).debug(
        `ClassName "${classNameBit}" is ignored during conversion to legacy styles.`,
      )

      return
    }

    const MARGIN_CLASS_NAMES_PREFIX: Array<string> = [
      'm-',
      'mb-',
      'ml-',
      'mr-',
      'mt-',
      'mx-',
      'my-',
    ]
    if (
      MARGIN_CLASS_NAMES_PREFIX.some((prefix) =>
        classNameBit.startsWith(prefix),
      )
    ) {
      ;(logger ?? console).warn(
        `ClassName "${classNameBit}" isn't properly supported on Outlook.com mail client!`,
      )

      const direction = classNameBit.split('-')[0]?.replace('m', '')
      const value = classNameBit.split('-')[1]
      if (isEmpty(value)) {
        return
      }

      const propertiesNames: Array<keyof typeof styles> = []
      switch (direction) {
        case 'b': {
          propertiesNames.push('marginBottom')
          break
        }

        case 'l': {
          propertiesNames.push('marginLeft')
          break
        }

        case 'r': {
          propertiesNames.push('marginRight')
          break
        }

        case 't': {
          propertiesNames.push('marginTop')
          break
        }

        case 'x': {
          propertiesNames.push('marginLeft', 'marginRight')
          break
        }

        case 'y': {
          propertiesNames.push('marginBottom', 'marginTop')
          break
        }

        default: {
          propertiesNames.push('margin')
        }
      }

      propertiesNames.forEach((propertyName) => {
        let cssValue: string = ''
        switch (value) {
          case 'auto': {
            cssValue = 'auto'
            break
          }

          case 'half': {
            cssValue = `${BASE_SPACING_VALUE / 2}px`
            break
          }

          case 'on-and-half': {
            cssValue = `${Math.round(BASE_SPACING_VALUE * 1.5)}px`
            break
          }

          case 'one-and-half': {
            cssValue = `${BASE_SPACING_VALUE / 2}px`
            break
          }

          case 'px': {
            cssValue = '1px'
            break
          }

          case 'quarter': {
            cssValue = `${BASE_SPACING_VALUE / 4}px`
            break
          }

          default: {
            const numberValue = Number.parseFloat(value)
            if (!isNumber(numberValue)) {
              ;(logger ?? console).error(
                `Value "${value}" isn't a valid margin value`,
              )

              return
            }

            cssValue = `${Math.round(numberValue * BASE_SPACING_VALUE)}px`
          }
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - This type is too complex for TS to handle.
        styles[propertyName] = cssValue
      })

      return
    }

    const PADDING_CLASS_NAMES_PREFIX: Array<string> = [
      '-p-',
      '-pb-',
      '-pl-',
      '-pr-',
      '-pt-',
      '-px-',
      '-py-',
      'p-',
      'pb-',
      'pl-',
      'pr-',
      'pt-',
      'px-',
      'py-',
    ]
    if (
      PADDING_CLASS_NAMES_PREFIX.some((prefix) =>
        classNameBit.startsWith(prefix),
      )
    ) {
      ;(logger ?? console).warn(
        `ClassName "${classNameBit}" isn't properly supported on Outlook.com mail client!`,
      )

      const isNegative = classNameBit.startsWith('-')
      const absoluteClassNameBit = isNegative
        ? classNameBit.slice(1)
        : classNameBit

      const direction = absoluteClassNameBit.split('-')[0]?.replace('m', '')
      const value = absoluteClassNameBit.split('-')[1]
      if (isEmpty(value)) {
        return
      }

      const propertiesNames: Array<keyof typeof styles> = []
      switch (direction) {
        case 'b': {
          propertiesNames.push('paddingBottom')
          break
        }

        case 'l': {
          propertiesNames.push('paddingLeft')
          break
        }

        case 'r': {
          propertiesNames.push('paddingRight')
          break
        }

        case 't': {
          propertiesNames.push('paddingTop')
          break
        }

        case 'x': {
          propertiesNames.push('paddingLeft', 'paddingRight')
          break
        }

        case 'y': {
          propertiesNames.push('paddingBottom', 'paddingTop')
          break
        }

        default: {
          propertiesNames.push('padding')
        }
      }

      propertiesNames.forEach((propertyName) => {
        let cssValue: string = ''
        switch (value) {
          case 'auto': {
            cssValue = 'auto'
            break
          }

          case 'half': {
            cssValue = `${(BASE_SPACING_VALUE / 2) * (isNegative ? -1 : 1)}px`
            break
          }

          case 'on-and-half': {
            cssValue = `${Math.round(BASE_SPACING_VALUE * 1.5) * (isNegative ? -1 : 1)}px`
            break
          }

          case 'one-and-half': {
            cssValue = `${(BASE_SPACING_VALUE / 2) * (isNegative ? -1 : 1)}px`
            break
          }

          case 'px': {
            cssValue = isNegative ? '-1px' : '1px'
            break
          }

          case 'quarter': {
            cssValue = `${(BASE_SPACING_VALUE / 4) * (isNegative ? -1 : 1)}px`
            break
          }

          default: {
            const numberValue = Number.parseFloat(value)
            if (!isNumber(numberValue)) {
              ;(logger ?? console).error(
                `Value "${isNegative ? '-' : ''}${value}" isn't a valid padding value`,
              )

              return
            }

            cssValue = `${Math.round(numberValue * BASE_SPACING_VALUE) * (isNegative ? -1 : 1)}px`
          }
        }

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore - This type is too complex for TS to handle.
        styles[propertyName] = cssValue
      })

      return
    }

    switch (classNameBit) {
      case 'transform-this-class': {
        break
      }

      default: {
        if (classNameBit.startsWith('preserve-legacy-')) {
          break
        }

        ;(logger ?? console).warn(
          `ClassName "${classNameBit}" doesn't have any legacy counterpart. Please handle "${classNameBit}" in the \`classNameToLegacyStyle\` helper!`,
        )
      }
    }
  })

  return {
    ...styles,
    ...extendedStyles,
  }
}
